import { Children, isValidElement, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./Carousel.css";

type CarouselProps = {
  /** Nome acessível da faixa. */
  label: string;
  children: React.ReactNode;
  /** "row": itens alinhados à esquerda. "center": item ativo no meio, vizinhos menores e esmaecidos. */
  variant?: "row" | "center";
  /** "sides": setas nas laterais. "top": setas numa barra acima da faixa, à direita de `head`. */
  arrows?: "sides" | "top";
  head?: React.ReactNode;
  dots?: boolean;
  /** Item ativo ao montar (variant "center"). */
  initial?: number;
  /** Passa sozinho para o próximo item a cada N ms e volta ao primeiro no fim, sem parar. */
  autoplay?: number;
  className?: string;
};

const behavior = (): ScrollBehavior =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

// Faixa horizontal com scroll-snap: arrasta no toque, rola no trackpad e no teclado (com foco na faixa),
// e tem setas e bolinhas para quem usa mouse.
export default function Carousel({
  label,
  children,
  variant = "row",
  arrows = "sides",
  head,
  dots = false,
  initial = 0,
  autoplay,
  className = "",
}: CarouselProps) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const items = Children.toArray(children);
  const [active, setActive] = useState(initial);
  const activeRef = useRef(initial);
  const [edges, setEdges] = useState({ start: true, end: false });

  // scrollLeft que mostra o item i (no centro ou no início da faixa)
  const targetLeft = useCallback(
    (track: HTMLDivElement, el: HTMLElement) => {
      const max = track.scrollWidth - track.clientWidth;
      const left =
        variant === "center"
          ? el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2
          : el.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || "0");
      return Math.max(0, Math.min(max, left));
    },
    [variant]
  );

  const goTo = useCallback(
    (i: number, how: ScrollBehavior = behavior()) => {
      const track = trackRef.current;
      const el = track?.children[i] as HTMLElement | undefined;
      if (!track || !el) return;
      track.scrollTo({ left: targetLeft(track, el), behavior: how });
    },
    [targetLeft]
  );

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const els = Array.from(track.children) as HTMLElement[];
    let best = 0;
    let dist = Infinity;
    els.forEach((el, i) => {
      const d = Math.abs(targetLeft(track, el) - track.scrollLeft);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    // no fim da faixa os últimos itens não chegam ao início: o ativo passa a ser o último
    if (variant === "row" && max > 2 && track.scrollLeft >= max - 2) best = els.length - 1;
    activeRef.current = best;
    setActive(best);
    setEdges({ start: track.scrollLeft <= 2, end: track.scrollLeft >= max - 2 });
  }, [targetLeft, variant]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (variant === "center") goTo(initial, "auto");
    update();
    let raf = 0;
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    // mudou a largura: mantém o item ativo no lugar
    const ro = new ResizeObserver(() => {
      if (variant === "center") goTo(activeRef.current, "auto");
      update();
    });
    ro.observe(track);
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      track.removeEventListener("scroll", onScroll);
    };
  }, [goTo, update, variant, initial]);

  // `active` nas dependências: navegar à mão reinicia a contagem, para não pular logo depois de um clique
  useEffect(() => {
    if (!autoplay || items.length < 2) return;
    const id = window.setInterval(() => goTo((activeRef.current + 1) % items.length), autoplay);
    return () => window.clearInterval(id);
  }, [autoplay, active, items.length, goTo]);

  const step = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    if (variant === "center") goTo(active + dir);
    else track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: behavior() });
  };

  const prev = (
    <button
      type="button"
      className="cz-carousel__arrow cz-carousel__arrow--prev"
      aria-label={t("home.carousel_prev", "Anterior")}
      disabled={edges.start}
      onClick={() => step(-1)}
    >
      <ChevronLeft size={20} aria-hidden="true" />
    </button>
  );
  const next = (
    <button
      type="button"
      className="cz-carousel__arrow cz-carousel__arrow--next"
      aria-label={t("home.carousel_next", "Próximo")}
      disabled={edges.end}
      onClick={() => step(1)}
    >
      <ChevronRight size={20} aria-hidden="true" />
    </button>
  );

  const isStatic = edges.start && edges.end;
  const classes = `cz-carousel cz-carousel--${variant} cz-carousel--arrows-${arrows}${isStatic ? " is-static" : ""} ${className}`.trim();

  return (
    <div className={classes} style={autoplay ? ({ "--cz-autoplay": `${autoplay}ms` } as React.CSSProperties) : undefined}>
      {arrows === "top" && (
        <div className="cz-carousel__bar">
          {head}
          <div className="cz-carousel__nav">
            {prev}
            {next}
          </div>
        </div>
      )}
      <div className="cz-carousel__viewport">
        {arrows === "sides" && prev}
        <div ref={trackRef} className="cz-carousel__track" role="region" aria-label={label} tabIndex={0}>
          {items.map((child, i) => (
            <div
              key={isValidElement(child) && child.key != null ? child.key : i}
              className={`cz-carousel__item${i === active ? " is-active" : ""}`}
              onClickCapture={(e) => {
                // no modo centro, clicar num vizinho traz ele para o meio em vez de seguir o link
                if (variant !== "center" || i === active) return;
                e.preventDefault();
                e.stopPropagation();
                goTo(i);
              }}
            >
              {child}
            </div>
          ))}
        </div>
        {arrows === "sides" && next}
      </div>
      {dots && (
        <div className={`cz-carousel__dots${autoplay ? " cz-carousel__dots--progress" : ""}`}>
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`cz-carousel__dot${i === active ? " is-active" : ""}`}
              aria-label={t("home.carousel_go", { n: i + 1, defaultValue: "Ir para o item {{n}}" })}
              aria-current={i === active ? "true" : undefined}
              onClick={() => goTo(i)}
            >
              {/* com autoplay, a bolinha ativa enche até a troca; nasce de novo a cada item, reiniciando a animação */}
              {autoplay && i === active && <span className="cz-carousel__dot-fill" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
